import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Fade,
  Slide,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Link,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ExpandMore, Star, Psychology, TrendingUp, Favorite, AutoAwesome, Security } from '@mui/icons-material';
import { theme as appTheme, spacing, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';
import { useAuth } from '../../context/AuthContext';
// 已移除支付：删除 creemApi 相关引用

// 移除 SEOHead 及结构化数据模板，不再注入复杂 SEO 头部

// 导入功能图片
import astrolabeImage from '../../assets/images/紫微斗数排盘.png';
import aiConsultationImage from '../../assets/images/AI问答.png';
import reportsImage from '../../assets/images/运势报告.png';
import soulmatePaintingImage from '../../assets/images/真爱画像.png';

interface WelcomeScreenProps {}

// CSS animations
const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const WelcomeScreen: React.FC<WelcomeScreenProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  // 支付暂不接入

  // 支付暂不接入：移除支付回调处理

  const handleGetStarted = () => {
    navigate('/auth/register');
  };

  const handleSignIn = () => {
    navigate('/auth/login');
  };

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  // 支付暂不接入：移除订阅支付处理

  const features = [
    {
      image: astrolabeImage,
      title: '个人星盘'
    },
    {
      image: aiConsultationImage,
      title: 'AI算命师'
    },
    {
      image: reportsImage,
      title: '运势报告'
    },
    {
      image: soulmatePaintingImage,
      title: '灵魂伴侣画像'
    }
  ];

  const pricingPlans = [
    {
      name: '免费计划',
      price: '免费',
      features: ['基础星盘生成', '每日运势查看', '2次AI咨询'],
      popular: false,
      buttonText: '选择计划',
      onClick: handleGetStarted,
      isPremium: false
    }
  ];

  const faqs = [
    {
      question: '什么是紫微斗数？',
      answer: '紫微斗数是中国古代占星学的一种，通过分析出生时间和地点来预测个人命运和性格特征。'
    },
    {
      question: 'AI算命师准确吗？',
      answer: '我们的AI基于传统紫微斗数理论训练，结合现代数据分析技术，提供个性化的命理解读。'
    },
    {
      question: '如何生成我的星盘？',
      answer: '只需提供准确的出生日期、时间和地点，系统会自动为您生成详细的紫微斗数星盘。'
    },
    {
      question: '订阅可以随时取消吗？',
      answer: '是的，您可以随时在账户设置中取消订阅，取消后仍可使用至当前计费周期结束。'
    },
    {
      question: '支持哪些支付方式？',
      answer: '我们支持信用卡、借记卡等多种支付方式，所有支付都通过安全加密处理。'
    }
  ];

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, var(--primary-purple) 0%, var(--gradient-purple) 50%, var(--primary-purple) 100%)',
          position: 'relative',
        }}
      >
      {/* 移除顶部语言选择器 */}
      
      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '10%', sm: '15%' },
          left: { xs: '2%', sm: '5%' },
          fontSize: { xs: 20, sm: 30 },
          opacity: { xs: 0.15, sm: 0.2 },
          animation: `${rotateAnimation} 20s linear infinite`,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        ☯
      </Box>
      
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '15%', sm: '20%' },
          right: { xs: '2%', sm: '5%' },
          fontSize: { xs: 20, sm: 30 },
          opacity: { xs: 0.15, sm: 0.2 },
          animation: `${rotateAnimation} 25s linear infinite`,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        🐉
      </Box>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, sm: 12 }, pb: 8 }}>
        <Fade in timeout={1500}>
          <Box
            sx={{
              textAlign: 'center',
              mb: 8,
              animation: `${floatAnimation} 3s ease-in-out infinite`,
            }}
          >
            <Typography
              sx={{
                fontSize: 50,
                mb: 2,
                display: 'block',
              }}
            >
              ✨
            </Typography>
            
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                fontWeight: 'bold',
                color: '#FFFFFF',
                textAlign: 'center',
                mb: 2,
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              }}
            >
              紫微斗数
            </Typography>
            
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                color: '#E6E6FA',
                textAlign: 'center',
                mb: 2,
                fontWeight: 300,
              }}
            >
              解锁您的命运密码
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                color: '#FFD700',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 4,
              }}
            >
              传统紫微斗数 × 现代AI技术
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 }, 
              justifyContent: 'center', 
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              maxWidth: { xs: '300px', sm: 'none' },
              mx: 'auto'
            }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: appTheme.colors.primary,
                  py: { xs: 1.2, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  borderRadius: 25,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                  minWidth: { xs: '200px', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                开始探索
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleSignIn}
                sx={{
                  color: '#FFFFFF',
                  borderColor: '#FFFFFF',
                  py: { xs: 1.2, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  borderRadius: 25,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  minWidth: { xs: '200px', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.1),
                    borderColor: '#FFFFFF',
                  },
                }}
              >
                登录
              </Button>
            </Box>
            
            {/* Powered by iztro */}
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                mt: 3,
                fontSize: '0.9rem',
              }}
            >
              powered by{' '}
              <Link
                 href="https://github.com/SylarLong/iztro"
                 target="_blank"
                 rel="noopener noreferrer"
                 sx={{
                   color: 'rgba(255, 255, 255, 0.8)',
                   textDecoration: 'underline',
                   '&:hover': {
                     color: '#FFFFFF',
                     textDecoration: 'underline',
                   },
                 }}
               >
                iztro
              </Link>
            </Typography>
          </Box>
        </Fade>
      </Container>

      {/* Features Section */}
      <Box sx={{ backgroundColor: alpha('#FFFFFF', 0.05), py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: '#FFFFFF',
              textAlign: 'center',
              mb: { xs: 3, md: 6 },
              px: { xs: 2, sm: 0 }
            }}
          >
            核心功能
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              mb: { xs: 4, md: 6 },
              color: '#E6E6FA',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            探索紫微斗数的神秘世界
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
            {features.map((feature, index) => (
              <Slide direction="up" in timeout={1500 + index * 200} key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 300, sm: 400, md: 500 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 3,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      p: { xs: 2, md: 3 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                </Box>
              </Slide>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: '#FFFFFF',
              textAlign: 'center',
              mb: { xs: 3, md: 6 },
              px: { xs: 2, sm: 0 }
            }}
          >
            价格方案
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              mb: { xs: 4, md: 6 },
              color: '#E6E6FA',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            选择适合您的服务套餐
          </Typography>

          {/* 支付暂不接入：移除错误提示 */}
          
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: plan.popular ? alpha('#FFD700', 0.1) : alpha('#FFFFFF', 0.1),
                    backdropFilter: 'blur(10px)',
                    border: plan.popular ? '2px solid #FFD700' : '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    position: 'relative',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label={"最受欢迎"}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#FFD700',
                        color: '#000',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        mb: 2,
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        color: plan.popular ? '#FFD700' : '#FFFFFF',
                        fontWeight: 'bold',
                        mb: 3,
                      }}
                    >
                      {plan.price}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          sx={{
                            color: '#E6E6FA',
                            mb: 1,
                            textAlign: 'left',
                          }}
                        >
                          • {feature}
                        </Typography>
                      ))}
                    </Box>
                    <Button
                      fullWidth
                      variant={plan.popular ? 'contained' : 'outlined'}
                      onClick={plan.onClick}
                      startIcon={undefined}
                      sx={{
                        py: 1.5,
                        borderRadius: 25,
                        fontWeight: 'bold',
                        ...(plan.popular ? {
                          backgroundColor: '#FFD700',
                          color: '#000',
                          '&:hover': {
                            backgroundColor: '#FFC107',
                          },
                          '&:disabled': {
                            backgroundColor: '#FFC107',
                            color: '#666',
                          },
                        } : {
                          color: '#FFFFFF',
                          borderColor: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: alpha('#FFFFFF', 0.1),
                          },
                        }),
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: alpha('#FFFFFF', 0.05), py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: '#FFFFFF',
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              px: { xs: 2, sm: 0 }
            }}
          >
            常见问题
          </Typography>
          
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === `panel${index}`}
              onChange={handleFaqChange(`panel${index}`)}
              sx={{
                backgroundColor: alpha('#FFFFFF', 0.1),
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 2,
                mb: 2,
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#FFFFFF' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#E6E6FA',
                    lineHeight: 1.6,
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: appTheme.colors.primary,
          py: { xs: 4, md: 6 },
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                博客
              </Typography>
              <Link
                component="button"
                onClick={() => navigate('/blog/what-is-ziwei-doushu')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  mb: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                什么是紫微斗数
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/blog/astrology-glossary')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  mb: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                占星术词汇表
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/blog')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                所有文章
              </Link>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                功能
              </Typography>
              <Link
                component="button"
                onClick={() => navigate('/features')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  mb: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                探索紫微斗数的神秘世界
              </Link>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                关于
              </Typography>
              <Link
                component="button"
                onClick={() => navigate('/legal/privacy-policy')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  mb: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                隐私政策
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/legal/terms-of-service')}
                sx={{
                  color: '#E6E6FA',
                  textDecoration: 'none',
                  display: 'block',
                  mb: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#FFFFFF',
                    textDecoration: 'underline',
                  },
                }}
              >
                服务条款
              </Link>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <Typography
            variant="body2"
            sx={{
              color: '#E6E6FA',
              textAlign: 'center',
            }}
          >
            © 2025 紫微AI — 版权所有
          </Typography>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default WelcomeScreen;